import { useState, useEffect, useCallback } from 'react'
import { colors, btnPrimary } from '../../components/ui'
import { getAuthToken } from '../../services/api'
import { ConfigTreeMerge } from './ConfigTreeMerge'

interface DeviceSnapshot {
  readonly siteId: string | null
  readonly deviceId: number
  readonly instanceSeq: number
  readonly deviceName: string
  readonly configDriftDetectedAt: string | null
}

interface ConfigDetail {
  readonly reportedConfig: Record<string, unknown> | null
  readonly reportedConfigHash: string | null
  readonly centralConfig: Record<string, unknown> | null
  readonly centralConfigVersion: number | null
  readonly centralConfigHash: string | null
  readonly driftDetectedAt: string | null
}

interface Props {
  readonly device: DeviceSnapshot
}

type View = 'comparison' | 'merge'

const bannerStyle = (type: 'drift' | 'synced' | 'bootstrap'): React.CSSProperties => ({
  padding: '0.75rem 1rem',
  borderRadius: '4px',
  marginBottom: '1rem',
  fontSize: '13px',
  fontWeight: 500,
  background: type === 'synced' ? '#d1fae5' : type === 'bootstrap' ? '#dbeafe' : '#fef3c7',
  color: type === 'synced' ? '#065f46' : type === 'bootstrap' ? '#1e40af' : '#92400e',
  border: `1px solid ${type === 'synced' ? '#a7f3d0' : type === 'bootstrap' ? '#bfdbfe' : '#fde68a'}`,
})

export function ConfigComparisonView({ device }: Props) {
  const [detail, setDetail] = useState<ConfigDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [view, setView] = useState<View>('comparison')
  const [actionLoading, setActionLoading] = useState(false)

  const fetchDetail = useCallback(async () => {
    setLoading(true)
    setError(null)
    const token = getAuthToken()
    try {
      const siteParam = device.siteId ? `?siteId=${encodeURIComponent(device.siteId)}` : '?'
      const res = await fetch(
        `/api/fleet/devices/${device.deviceId}/config${siteParam}&instanceSeq=${device.instanceSeq}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      )
      if (!res.ok) throw new Error(`Failed to load config: ${res.status}`)
      const json = await res.json()
      setDetail(json.data)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [device.deviceId, device.instanceSeq, device.siteId])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  const handleAdopt = async () => {
    if (!detail?.reportedConfig) return
    setActionLoading(true)
    const token = getAuthToken()
    try {
      const res = await fetch(`/api/fleet/devices/${device.deviceId}/adopt`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: 'reported',
          configJson: detail.reportedConfig,
          siteId: device.siteId,
          udmDeviceId: device.deviceId,
          instanceSeq: device.instanceSeq,
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Adopt failed: ${res.status}`)
      }
      await fetchDetail()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Adopt failed')
    } finally {
      setActionLoading(false)
    }
  }

  const handleMergeSave = async (mergedConfig: Record<string, unknown>) => {
    setActionLoading(true)
    const token = getAuthToken()
    try {
      const res = await fetch(`/api/fleet/devices/${device.deviceId}/adopt`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source: 'merged',
          configJson: mergedConfig,
          siteId: device.siteId,
          udmDeviceId: device.deviceId,
          instanceSeq: device.instanceSeq,
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { error?: string }).error ?? `Save failed: ${res.status}`)
      }
      setView('comparison')
      await fetchDetail()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) return <div style={{ color: colors.textMuted, padding: '1rem' }}>Loading config...</div>
  if (error) return <div style={{ color: colors.danger, padding: '1rem' }}>{error}</div>
  if (!detail) return <div style={{ color: colors.textMuted, padding: '1rem' }}>No config data available.</div>

  const hasCentral = detail.centralConfig !== null
  const hasReported = detail.reportedConfig !== null
  const isDrift = detail.driftDetectedAt !== null

  const bannerType = !hasReported ? 'synced' : !hasCentral ? 'bootstrap' : isDrift ? 'drift' : 'synced'
  const bannerText = !hasReported
    ? 'Device has not reported its configuration yet.'
    : !hasCentral
      ? 'No Central config exists. Device config available for adoption.'
      : isDrift
        ? `Config drift detected since ${new Date(detail.driftDetectedAt!).toLocaleString()}`
        : 'Configs are in sync.'

  const preStyle: React.CSSProperties = {
    background: colors.bgMuted,
    padding: '0.75rem',
    borderRadius: '4px',
    fontSize: '11px',
    overflow: 'auto',
    maxHeight: '400px',
    border: `1px solid ${colors.border}`,
  }

  return (
    <div>
      <div style={bannerStyle(bannerType)}>{bannerText}</div>

      {view === 'comparison' && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {hasReported && (
              <button
                type="button"
                onClick={handleAdopt}
                disabled={actionLoading}
                style={{
                  ...btnPrimary,
                  opacity: actionLoading ? 0.6 : 1,
                  background: !hasCentral ? colors.accent : '#fff',
                  color: !hasCentral ? '#fff' : colors.accent,
                  border: `1px solid ${colors.accent}`,
                }}
              >
                {actionLoading ? 'Adopting...' : 'Adopt Reported Config'}
              </button>
            )}
            {hasCentral && hasReported && isDrift && (
              <button
                type="button"
                onClick={() => setView('merge')}
                style={{
                  ...btnPrimary,
                  background: '#fff',
                  color: colors.accent,
                  border: `1px solid ${colors.accent}`,
                }}
              >
                Open Merge Editor
              </button>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <h4 style={{ margin: '0 0 0.5rem', fontSize: '13px', color: colors.textMuted }}>
                Central Config {hasCentral ? `(v${detail.centralConfigVersion})` : ''}
              </h4>
              <pre style={preStyle}>
                {hasCentral ? JSON.stringify(detail.centralConfig, null, 2) : 'No Central config.'}
              </pre>
            </div>
            <div>
              <h4 style={{ margin: '0 0 0.5rem', fontSize: '13px', color: colors.textMuted }}>Reported Config</h4>
              <pre style={preStyle}>
                {hasReported ? JSON.stringify(detail.reportedConfig, null, 2) : 'Device has not reported config.'}
              </pre>
            </div>
          </div>
        </>
      )}

      {view === 'merge' && hasCentral && hasReported && (
        <ConfigTreeMerge
          centralConfig={detail.centralConfig!}
          reportedConfig={detail.reportedConfig!}
          onSave={handleMergeSave}
          onCancel={() => setView('comparison')}
          saving={actionLoading}
        />
      )}
    </div>
  )
}
